/**
 * Abschluss-Zertifikat (App) — Stand laden + Urkunde herunterladen.
 * Spiegelt den Web-Vertrag (public/assets/certificate.js): GET
 * /api/certificate/status; bei erreichtem Großziel einmalig eine Gratulation
 * (Toast); Download als PDF-Blob mit Bearer-Header.
 */
import { signal } from "@preact/signals";
import { config } from "../config";
import { endpoints } from "../api/endpoints";
import type { CertificateStatusResponse } from "../api/types";
import { getToken } from "./session";

export const certificateStatus = signal<CertificateStatusResponse | null>(null);
/** Wird gesetzt, wenn das Großziel ERSTMALIG erreicht wird → Abschluss-Modal. */
export const certModal = signal<CertificateStatusResponse | null>(null);

const ACK_KEY = "ki-kurs-cert-ack";

function acked(id: string): boolean {
  try {
    return (JSON.parse(localStorage.getItem(ACK_KEY) || "[]") as string[]).includes(id);
  } catch {
    return false;
  }
}
function ack(id: string): void {
  try {
    const a = JSON.parse(localStorage.getItem(ACK_KEY) || "[]") as string[];
    if (!a.includes(id)) {
      a.push(id);
      localStorage.setItem(ACK_KEY, JSON.stringify(a));
    }
  } catch {
    /* ignore */
  }
}

/** Status laden; bei erstmalig erreichtem Großziel eine Gratulation zeigen. */
export async function loadCertificateStatus(courseId?: string): Promise<void> {
  try {
    const res = await endpoints.certificateStatus(courseId);
    certificateStatus.value = res;
    if (res.goal_reached) {
      // Stabile ACK-Kennung: pro Kurs (die Zertifikats-ID kann anfangs noch fehlen
      // und später nachkommen → würde das Modal sonst erneut auslösen).
      const id = `course:${courseId ?? "default"}`;
      if (!acked(id)) {
        ack(id);
        // Abschluss-Modal statt nur Toast (Web-Parität: certificate.js zeigt ein Modal).
        certModal.value = res;
      }
    }
  } catch {
    /* offline o. ä. — Stand bleibt */
  }
}

/** Urkunde als PDF herunterladen (Blob mit Bearer-Header). */
export async function downloadCertificate(): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(`${config.apiBaseUrl}/api/certificate/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return false;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Sentinus-KI-Kurs_Zertifikat.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return true;
  } catch {
    return false;
  }
}
